import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import { definitionLabel } from 'styles/base-classes';
import ListLoader from 'components/utility/ContentLoader/ListLoader';
import { EntityContext } from 'context/entityContext';
import {
  ICountryMetadata,
  IRadioSpins,
} from 'components/data-containers/KPIs/types';

const TopTerritories = styled.section`
  display: flex;
  flex-flow: column nowrap;
`;

const TopTerritoriesLabel = styled.h4`
  ${definitionLabel};
`;

const ListWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;

const ListCitiesWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopTerritoriesList = styled.ol`
  padding: 0 0 1em;
  margin-right: 1rem;
  list-style: none;
  color: ${({ theme }): string => theme.NightBlue};
`;

interface ITopTerritoriesItem {
  [key: string]: any;
}

export const TopTerritoriesItem = styled.li<ITopTerritoriesItem>`
  display: flex;
  align-items: center;
  color: ${({ dataColor }): any => dataColor};
  font-weight: ${({ fontWeight }): number => fontWeight};

  &::before {
    content: '#' attr(data-rank);
    display: inline-block;
    min-width: 1.2rem;
    margin-right: 1rem;
    color: ${({ theme }): string => theme.NightBlue};
    font-weight: ${({ fontWeight }): number => fontWeight};
    text-align: right;
  }
`;

const LoaderWrapper = styled.div`
  svg {
    width: 100%;
    max-width: 9.375em;
  }
`;

/**
 * SpinsTopTerritories displays a list of top territories (countries or
 * US markets) for each entity.
 *
 * @param {object} props React props
 * @param {function} props.getKey Returns a string to use as a React key
 * @param {string} props.label Label to display above the list
 * @param {boolean} props.pending Is the fetch for this component pending?
 * @param {Array.<object>} props.topTerritories Array of top territory data for each entity
 * @param {object} props.TerritoryItem React Component to use to display the territory items
 * @param {string} props.type Type of topTerritories, for instance, 'city'
 */

interface IRadioSpinsTopTerritoriesProps {
  getKey: (entity: IRadioSpins, item: ICountryMetadata) => string;
  label: string;
  pending: boolean;
  topTerritories: any;
  TerritoryItem: Function;
  type?: string;
}

const RadioSpinsTopTerritories: React.FC<IRadioSpinsTopTerritoriesProps> = (
  props
) => {
  const { getKey, label, pending, topTerritories, TerritoryItem, type } = props;

  const ctx = useContext(EntityContext);
  const { compareMode } = ctx;
  const List = type === 'city' ? ListCitiesWrap : ListWrapper;

  return (
    <TopTerritories>
      <TopTerritoriesLabel>{label}</TopTerritoriesLabel>

      <List>
        {pending && (
          <LoaderWrapper>
            <ListLoader
              len={5}
              itemWidth={150}
              itemHeight={16}
              itemMargin={8}
            />
          </LoaderWrapper>
        )}

        {topTerritories &&
          topTerritories.map((entity: IRadioSpins) => (
            <TopTerritoriesList key={entity.entity.id}>
              {entity.data.map((item: ICountryMetadata, itemIndex: number) => (
                <TerritoryItem
                  compareMode={compareMode}
                  entity={entity.entity}
                  rank={itemIndex + 1}
                  item={item}
                  key={getKey(entity, item)}
                />
              ))}
            </TopTerritoriesList>
          ))}
      </List>
    </TopTerritories>
  );
};

export default RadioSpinsTopTerritories;
